import React from 'react'
import { matchPath, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks'
import { DashboardLayout } from '@/layouts'
import { AccessDeniedState, ErrorState, PageLoader } from '@/components'
import { isFeatureEnabled } from '@/utils/helper/feature-visibility'
import { canViewSalesTeamWideData } from '@/utils/staffAccessPolicy'
import routes, { RouteType } from './route'

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const {
    isLogin,
    isLoading,
    tenantId,
    profile,
    roleAndPermissions,
    hasPermission,
    isCreditFeatureEnabled: isCreditEnabled,
    isLoyaltyFeatureEnabled: isLoyaltyEnabled,
    isAiCoachingEnabled,
    isLactateTestEnabled,
    isWorkoutBuilderEnabled,
    hasBootstrapError,
    retryBootstrap,
    logout,
  } = useAuth()
  const storedTenantId = tenantId ?? localStorage.getItem('tenantId')
  const location = useLocation()

  if (isLoading) {
    return <PageLoader />
  }

  if (!isLogin) {
    return <Navigate to="/login" replace />
  }

  if (!storedTenantId && location.pathname !== '/select-tenant') {
    return <Navigate to="/select-tenant" replace />
  }

  if (location.pathname === '/select-tenant') {
    if (storedTenantId) {
      return <Navigate to="/" replace />
    }
    return <>{children}</>
  }

  // Tenant-scoped bootstrap failed after retries (e.g. backend 500 / cold
  // start, or a tenant switch whose bootstrap couldn't load). Show a
  // recoverable retry screen instead of an infinite spinner — or stale data
  // from a previous tenant — regardless of whether a prior profile is still
  // held in state. The session is still valid, so offer logout as an escape
  // hatch instead of forcing one.
  if (storedTenantId && hasBootstrapError) {
    return <ErrorState onRetry={retryBootstrap} onLogout={() => void logout()} minHeight="100vh" />
  }

  // Wait for tenant-scoped data (including feature flags) before applying feature guards.
  if (storedTenantId && !profile) {
    return <PageLoader />
  }

  const matchedRoute = routes.find(
    (route) =>
      (route.route_type as RouteType) === RouteType.PRIVATE &&
      !!matchPath({ path: route.path, end: true }, location.pathname)
  )

  if (
    !isFeatureEnabled(matchedRoute?.feature, {
      isCreditFeatureEnabled: isCreditEnabled,
      isLoyaltyFeatureEnabled: isLoyaltyEnabled,
      isAiCoachingEnabled,
      isLactateTestEnabled,
      isWorkoutBuilderEnabled,
    })
  ) {
    return <Navigate to="/" replace />
  }

  // Route-level guards. The permission code and the role gate are ANDed, and
  // share one denial screen: staying on the URL with a worded "no access"
  // message beats a silent redirect for an old bookmark (QTY-2069/2072).
  const deniedByPermission =
    !!matchedRoute?.permission &&
    !hasPermission(matchedRoute.permission.code, matchedRoute.permission.action)
  const deniedByRole =
    !!matchedRoute?.salesTeamLeadOnly && !canViewSalesTeamWideData(roleAndPermissions)

  if (deniedByPermission || deniedByRole) {
    return (
      <DashboardLayout>
        <AccessDeniedState />
      </DashboardLayout>
    )
  }

  return <DashboardLayout>{children}</DashboardLayout>
}

export default ProtectedRoute
